import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { TourService } from 'ngx-ui-tour-core';
import { KirigamiTourStepHostService } from './tour-step-host.service';
import { KirigamiTourBackdropComponent } from './kirigami-tour-backdrop.component';
import type { KirigamiTourStep } from './tour.types';

/**
 * The single, root-mounted tour popover + backdrop. Reads the active step from
 * `KirigamiTourStepHostService`, positions itself next to the anchor element
 * with `@floating-ui/dom`, and drives navigation through `TourService`.
 */
@Component({
  selector: 'kirigami-tour-step',
  standalone: true,
  imports: [KirigamiTourBackdropComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <kirigami-tour-backdrop />
    @if (host.active(); as v) {
      <div #pop class="kt-popover" role="dialog" aria-modal="true" [attr.aria-label]="v.step.title">
        <button class="kt-popover__close" data-test="skip" (click)="end()" aria-label="Close tour">
          ✕
        </button>
        <h3 class="kt-popover__title">{{ v.step.title }}</h3>
        @if (v.step.content) {
          <p class="kt-popover__content">{{ v.step.content }}</p>
        }
        <div class="kt-popover__footer">
          <span class="kt-popover__progress">{{ v.index + 1 }} of {{ v.total }}</span>
          <div class="kt-popover__actions">
            @if (v.index > 0) {
              <button class="kt-btn kt-btn--ghost" data-test="back" (click)="prev()">Back</button>
            }
            @if (requiresAnchorClick()) {
              <span class="kt-popover__hint">Click the highlighted button to continue</span>
            } @else {
              <button class="kt-btn kt-btn--primary" data-test="next" (click)="next()">
                {{ isLast() ? 'Done' : 'Next' }}
              </button>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .kt-popover {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1001;
        width: 320px;
        max-width: calc(100vw - 24px);
        padding: 16px;
        background: var(--k-surface, #fff);
        color: var(--k-text, #0f172a);
        border-radius: 12px;
        box-shadow: 0 12px 32px rgba(15, 23, 42, 0.24);
        font:
          14px/1.5 system-ui,
          sans-serif;
      }
      .kt-popover__close {
        position: absolute;
        top: 8px;
        right: 8px;
        border: 0;
        background: transparent;
        cursor: pointer;
        font-size: 14px;
        color: var(--k-text-muted, #64748b);
      }
      .kt-popover__title {
        margin: 0 0 8px;
        font-size: 16px;
        font-weight: 600;
      }
      .kt-popover__content {
        margin: 0 0 16px;
        color: var(--k-text-muted, #475569);
      }
      .kt-popover__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .kt-popover__progress {
        font-size: 12px;
        color: var(--k-text-muted, #94a3b8);
      }
      .kt-popover__hint {
        font-size: 12px;
        font-style: italic;
        color: var(--k-text-muted, #64748b);
      }
      .kt-popover__actions {
        display: flex;
        gap: 8px;
      }
      .kt-btn {
        border-radius: 8px;
        padding: 6px 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid transparent;
      }
      .kt-btn--ghost {
        background: transparent;
        border-color: var(--k-border, #e2e8f0);
        color: var(--k-text, #334155);
      }
      .kt-btn--primary {
        background: var(--k-primary, #4f46e5);
        color: #fff;
      }
    `,
  ],
})
export class KirigamiTourStepComponent {
  readonly host = inject(KirigamiTourStepHostService);
  // Optional so this host is safe to mount even when no app has registered the
  // engine via provideKirigamiTour(); navigation methods then no-op.
  private readonly tourService = inject(TourService, { optional: true });

  private readonly popoverRef = viewChild<ElementRef<HTMLElement>>('pop');

  /** True when the active step is the last one in the tour. */
  readonly isLast = computed(() => {
    const v = this.host.active();
    return !!v && v.index === v.total - 1;
  });

  /** True when the step advances only by clicking its anchor (nextOnAnchorClick);
   *  the popover then hides its Next button so the step can't be skipped. */
  readonly requiresAnchorClick = computed(() => !!this.host.active()?.step.nextOnAnchorClick);

  constructor() {
    // (Re)position whenever the active step or popover element changes.
    // autoUpdate returns its own teardown; register it with onCleanup so it
    // runs before the next effect execution and on destroy.
    effect((onCleanup) => {
      const v = this.host.active();
      const pop = this.popoverRef()?.nativeElement;
      if (!v || !pop) return;
      const step = v.step as KirigamiTourStep;

      // Centred steps skip anchored placement and pin to the viewport centre.
      // Used for anchors taller than the viewport, where top/bottom placement
      // would land the popover off-screen after the engine scrolls the anchor
      // into the middle. Re-centre on resize; the rAF backdrop loop keeps the
      // spotlight tracking the anchor independently.
      if (step.centered) {
        const center = () => {
          const x = Math.max(8, (window.innerWidth - pop.offsetWidth) / 2);
          const y = Math.max(8, (window.innerHeight - pop.offsetHeight) / 2);
          pop.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
        };
        center();
        window.addEventListener('resize', center);
        onCleanup(() => window.removeEventListener('resize', center));
        return;
      }

      const placement = step.placement ?? 'bottom';
      const stop = autoUpdate(
        v.anchorEl,
        pop,
        () => {
          computePosition(v.anchorEl, pop, {
            placement,
            middleware: [offset(10), flip(), shift({ padding: 8 })],
          }).then(({ x, y }) => {
            pop.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
          });
        },
        // Poll every animation frame so the popover follows layout shifts
        // (e.g. the instructions panel pushing the page), not just scroll/resize.
        { animationFrame: true },
      );
      onCleanup(stop);
    });
  }

  next(): void {
    if (this.isLast()) {
      this.tourService?.end();
    } else {
      this.tourService?.next();
    }
  }

  prev(): void {
    this.tourService?.prev();
  }

  end(): void {
    this.tourService?.end();
  }
}
